// src/syncService.ts
import { io } from "socket.io-client";
import {
  base64ToUint8Array,
  uint8ArrayToBase64,
  base64ArrayToUint8ArrayArray,
  resolveSyncPullPage
} from "@inkweaver/shared";
import { createSyncEngine, SYNC_REPLAY_ORIGIN } from "@inkweaver/sync-engine";
function createSyncService(deps) {
  const {
    localDB,
    authService,
    documentApi,
    socketUrl,
    clientId,
    socketConnectTimeoutMs = 5e3
  } = deps;
  let socket = null;
  const docRooms = /* @__PURE__ */ new Map();
  const connectionListeners = /* @__PURE__ */ new Set();
  const conflictListeners = /* @__PURE__ */ new Set();
  const apiClient = {
    /**
     * 分页拉取远端更新。
     * 失败重试 3 次（指数退避），总超时 120s；仅"还有更多数据"时才检查超时。
     */
    async pull(docId, cursor, limit, snapshotVersion) {
      const allUpdates = [];
      let currentCursor = cursor ?? 0;
      let hasMoreData = true;
      let snapshotResult;
      let retryCount = 0;
      const maxRetries = 3;
      const maxExecutionTime = 12e4;
      let iterationCount = 0;
      let latestUpdateId = cursor ?? 0;
      const startTime = Date.now();
      while (hasMoreData && retryCount < maxRetries && Date.now() - startTime < maxExecutionTime) {
        try {
          iterationCount++;
          const response = await documentApi.syncPull({
            docId,
            cursor: currentCursor,
            limit: limit ?? 100,
            snapshotVersion
          });
          if (!response || !Array.isArray(response.updates)) {
            retryCount++;
            if (retryCount >= maxRetries) {
              throw new Error("Sync pull \u8FD4\u56DE\u975E\u6CD5\u54CD\u5E94\u4E14\u91CD\u8BD5\u8017\u5C3D");
            }
            await delay(Math.pow(2, retryCount) * 1e3);
            continue;
          }
          if (iterationCount === 1 && response.snapshot) {
            snapshotResult = base64ToUint8Array(response.snapshot);
          }
          const updates = response.updates.map(
            (u) => base64ToUint8Array(u)
          );
          allUpdates.push(...updates);
          const progress = resolveSyncPullPage(currentCursor, {
            updateCount: updates.length,
            nextCursor: response.nextCursor,
            hasMore: response.hasMore,
            latestUpdateId: response.latestUpdateId
          });
          currentCursor = progress.nextCursor;
          latestUpdateId = progress.latestUpdateId;
          hasMoreData = progress.shouldContinue;
          retryCount = 0;
          if (!hasMoreData) break;
        } catch (error) {
          retryCount++;
          if (retryCount >= maxRetries) {
            throw error instanceof Error ? error : new Error("Sync pull failed after max retries");
          }
          await delay(Math.pow(2, retryCount) * 1e3);
        }
      }
      if (hasMoreData && Date.now() - startTime >= maxExecutionTime) {
        throw new Error("Sync pull exceeded max execution time");
      }
      return {
        updates: allUpdates,
        nextCursor: currentCursor,
        hasMore: false,
        latestUpdateId,
        snapshot: snapshotResult
      };
    },
    /** 推送本地 updates 到服务端，返回新的 latestUpdateId */
    async push(docId, updates, baseUpdateId) {
      if (updates.length === 0) return { latestUpdateId: baseUpdateId };
      const base64Updates = updates.map((u) => uint8ArrayToBase64(u));
      const response = await documentApi.syncPush({
        docId,
        updates: base64Updates,
        clientId,
        baseUpdateId
      });
      return { latestUpdateId: response.latestUpdateId };
    }
  };
  const syncEngine = createSyncEngine({
    localDB,
    api: apiClient,
    clientId
  });
  const notifyConnectionChange = (connected) => {
    connectionListeners.forEach((listener) => listener(connected));
  };
  const initSocket = () => {
    if (socket) return socket;
    socket = io(socketUrl, {
      transports: ["websocket", "polling"],
      autoConnect: false,
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 1e3,
      timeout: socketConnectTimeoutMs
    });
    socket.on("connect", () => {
      notifyConnectionChange(true);
      docRooms.forEach((ctx, docId) => {
        if (ctx.state === "joined") {
          socket?.emit("join-doc", { docId });
        }
      });
    });
    socket.on("disconnect", () => {
      notifyConnectionChange(false);
    });
    socket.on("update", ({ docId, data }) => {
      if (data?.clientId && data.clientId === clientId) {
        return;
      }
      const ctx = docRooms.get(docId);
      if (ctx && data.updates) {
        const decoded = base64ArrayToUint8ArrayArray(data.updates);
        ctx.listeners.forEach((listener) => listener(decoded));
      }
    });
    socket.on("conflict", ({ docId }) => {
      if (!docId) return;
      conflictListeners.forEach((cb) => cb(docId));
    });
    return socket;
  };
  const connectSocket = () => {
    return new Promise((resolve, reject) => {
      const connectWithToken = async () => {
        const tokens = await authService.getTokens();
        const s = initSocket();
        s.auth = { token: tokens?.access_token ?? "" };
        if (s.connected) {
          resolve();
          return;
        }
        const timer = setTimeout(() => {
          s.off("connect", onConnect);
          s.off("connect_error", onError);
          reject(new Error("WebSocket \u8FDE\u63A5\u8D85\u65F6"));
        }, socketConnectTimeoutMs);
        const onConnect = () => {
          clearTimeout(timer);
          s.off("connect_error", onError);
          resolve();
        };
        const onError = (err) => {
          clearTimeout(timer);
          s.off("connect", onConnect);
          reject(err);
        };
        s.once("connect", onConnect);
        s.once("connect_error", onError);
        s.connect();
      };
      connectWithToken().catch(reject);
    });
  };
  const joinDocRoomInternal = async (docId) => {
    try {
      await connectSocket();
      const ctx = docRooms.get(docId);
      if (!ctx || ctx.state !== "pending") return;
      socket?.emit("join-doc", { docId });
      ctx.state = "joined";
    } catch (error) {
      const ctx = docRooms.get(docId);
      if (ctx && ctx.state === "pending") {
        ctx.state = "idle";
      }
      console.warn("WebSocket \u672A\u8FDE\u63A5\uFF0C\u5C06\u4EC5\u4F7F\u7528 REST \u540C\u6B65:", error);
    }
  };
  const leaveDocRoomInternal = (docId) => {
    const ctx = docRooms.get(docId);
    if (!ctx) return;
    if (ctx.state === "joined") {
      try {
        if (socket?.connected) {
          socket.emit("leave-doc", { docId });
        }
      } catch (error) {
        console.warn("leave-doc \u5931\u8D25:", error);
      }
    }
    docRooms.delete(docId);
  };
  const subscribeDocRoom = (docId, onUpdate) => {
    let ctx = docRooms.get(docId);
    if (!ctx) {
      ctx = { state: "idle", listeners: /* @__PURE__ */ new Set() };
      docRooms.set(docId, ctx);
    }
    ctx.listeners.add(onUpdate);
    if (ctx.state === "idle") {
      ctx.state = "pending";
      void joinDocRoomInternal(docId);
    }
    return () => {
      const current = docRooms.get(docId);
      if (!current) return;
      current.listeners.delete(onUpdate);
      if (current.listeners.size === 0) {
        leaveDocRoomInternal(docId);
      }
    };
  };
  const syncDocument = async (docId) => {
    await syncEngine.syncDoc(docId);
  };
  const getYDoc = (docId) => syncEngine.getYDoc(docId);
  const clearYDocCache = (docId) => {
    syncEngine.clearYDocCache(docId);
  };
  const onSyncConflict = (callback) => {
    conflictListeners.add(callback);
    return () => conflictListeners.delete(callback);
  };
  const onConnectionChange = (callback) => {
    connectionListeners.add(callback);
    callback(isWebSocketConnected());
    return () => connectionListeners.delete(callback);
  };
  const isWebSocketConnected = () => socket?.connected ?? false;
  const disconnectSocket = () => {
    if (socket) {
      socket.disconnect();
      socket = null;
    }
    docRooms.clear();
    notifyConnectionChange(false);
  };
  return {
    subscribeDocRoom,
    syncDocument,
    getYDoc,
    clearYDocCache,
    onSyncConflict,
    onConnectionChange,
    isWebSocketConnected,
    disconnectSocket,
    get syncEngine() {
      return syncEngine;
    },
    get localDB() {
      return localDB;
    },
    get clientId() {
      return clientId;
    },
    get SYNC_REPLAY_ORIGIN() {
      return SYNC_REPLAY_ORIGIN;
    }
  };
}
var delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));
export {
  SYNC_REPLAY_ORIGIN,
  createSyncService
};
